import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Avatar, Button, Card, Flex, Spin, Typography, theme } from "antd";
import { useEffect, useState } from "react";

const { Text } = Typography;
const { useToken } = theme;

interface TCrypto {
  name: string;
  lastPrice: string;
  priceChangePercent: string;
  imageUrl: string;
  symbol: string;
}

export function DashboardCripto() {
  const { token } = useToken();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [cryptos, setCryptos] = useState<TCrypto[]>([]);
  const [showAll, setShowAll] = useState<boolean>(false); // Estado controla se mostra todos ou não

  const fetchCryptoData = async () => {
    // setLoading(true); // Comentado para não piscar o loading a cada refresh
    try {
      const coingeckoResponse = await fetch(
        "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=9&page=1"
      );
      // Adicionar tratamento de erro para status não-ok da response
      if (!coingeckoResponse.ok) {
        throw new Error(`HTTP error! status: ${coingeckoResponse.status}`);
      }
      const coingeckoData = await coingeckoResponse.json();

      if (!Array.isArray(coingeckoData)) {
        console.error("Formato inesperado da API CoinGecko:", coingeckoData);
        throw new Error("Formato de dados inválido da API.");
      }

      const formattedCryptos: TCrypto[] = coingeckoData.map((crypto: any) => {
        return {
          name: crypto.name ?? "N/A",
          symbol: (
            crypto.symbol ??
            crypto.id ??
            `unknown-${crypto.name}`
          ).toUpperCase(), // Garantir um symbol
          lastPrice: (crypto.current_price ?? 0).toString(),
          priceChangePercent: (
            crypto.price_change_percentage_24h ?? 0
          ).toString(),
          imageUrl: crypto.image ?? "", // Usar string vazia se não houver imagem
        };
      });

      setCryptos(formattedCryptos);
    } catch (error) {
      console.error("Erro ao buscar dados da CoinGecko:", error);
      // Poderia setar um estado de erro aqui para mostrar mensagem ao usuário
      // setCryptos([]); // Limpar ou manter os dados antigos?
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCryptoData();
    const interval = setInterval(fetchCryptoData, 20000);
    return () => clearInterval(interval);
  }, []);

  const itemsToShow = showAll ? cryptos : cryptos.slice(0, 4);
  const hasMoreItems = cryptos.length > 4;

  return (
    <Flex
      align="flex-start"
      style={{
        flexDirection: "column",
        width: "100%",
        borderRadius: token.borderRadiusLG,
        // backgroundColor: token.colorBgContainer,
        backdropFilter: "blur(40px)",
        border: `2px solid ${token.colorBorderSecondary}`,
        padding: token.paddingMD,
      }}
      gap={token.marginMD}
    >
      <Text
        style={{
          fontSize: token.fontSizeHeading4,
          fontWeight: token.fontWeightStrong,
          color: token.colorTextHeading,
        }}
      >
        Top Cryptos
      </Text>

      {/* Mostra Spin apenas no loading inicial absoluto */}
      {isLoading && cryptos.length === 0 ? (
        <Spin size="large" style={{ margin: "auto", alignSelf: "center" }} />
      ) : cryptos.length === 0 && !isLoading ? ( // Mensagem se não houver dados após o load
        <Text
          style={{ alignSelf: "center", color: token.colorTextDescription }}
        >
          Nenhuma criptomoeda encontrada.
        </Text>
      ) : (
        <>
          <Flex wrap="wrap" gap={10} style={{ width: "100%" }}>
            {itemsToShow.map((crypto) => (
              <Card
                key={crypto.symbol}
                style={{
                  width: "calc(50% - 5px)",
                  minWidth: "120px",
                  textAlign: "center",
                  height: "135px",
                  backgroundColor: "transparent",
                  border: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <Flex
                  align="center"
                  style={{ flexDirection: "column" }}
                  justify="space-between"
                  gap={4}
                >
                  <Avatar src={crypto.imageUrl} size="small">
                    {/* Fallback se imagem não carregar */}
                    {crypto.symbol?.[0]}
                  </Avatar>
                  <Typography.Text
                    style={{
                      fontSize: token.fontSizeSM,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100px",
                    }}
                    title={crypto.name}
                  >
                    {crypto.name}
                  </Typography.Text>
                  <Typography.Text
                    style={{
                      color:
                        parseFloat(crypto.priceChangePercent) >= 0
                          ? token.colorSuccess
                          : token.colorError,
                      fontSize: token.fontSizeSM,
                      fontWeight: token.fontWeightStrong,
                    }}
                  >
                    {parseFloat(crypto.priceChangePercent).toFixed(2)}%
                  </Typography.Text>
                </Flex>
                <Typography.Text
                  style={{
                    color: token.colorTextSecondary,
                    fontSize: token.fontSize,
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  ${parseFloat(crypto.lastPrice).toFixed(2)}
                </Typography.Text>
              </Card>
            ))}
          </Flex>
          {hasMoreItems && (
            <Flex justify="center" style={{ width: "100%" }}>
              <Button
                type="link"
                onClick={() => setShowAll(!showAll)}
                icon={showAll ? <UpOutlined /> : <DownOutlined />}
              >
                {showAll ? "Ver Menos" : "Ver Mais"}
              </Button>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
}
